// ActionNotification — the ACTION tier of the CTA-notification set (Nika
// 2026-09-02: "notifications should have CTA buttons that navigate to
// related field … create new component based on TutorialNotification, add
// buttons at the right — like Confirm/Goto and Cancel as vertical set —
// add this component in workspace-ui and reuse in Bellforge").
//
// FAMILY NOTE (notificationFamilyChrome.test.js, DESIGN_LANGUAGE.md §7.6):
// the chassis is the coachmark family's ("based on TutorialNotification" —
// canvas-card-glass + radiusControl, elevation carried by the theme), but
// dismissal is PER EVENT — the two buttons — never a cookie, so this card
// joins NEITHER locked family list. It is the third tier: a transient
// notification that asks for one decision and shows where the event went.
//
// CONTRACT. Content from state: { title, descr?, confirmText?, cancelText? }.
// Each of those four is KEY-IN-STATE (docs/I18N-CONVENTION.md): the state field
// may carry a bundle KEY or a plain server-supplied string, and every render
// site resolves it through polyglot — which returns a non-key verbatim, so a
// shell notification row's raw title still renders unchanged.
// Behaviour from mount-site props on the CARD:
//   onConfirmAction(ev, card, s) — absent → openNotificationTarget(s, card),
//     so a shell notification row ({ href, notifType, metadata }) drops in
//     unchanged and the Confirm button goes exactly where clicking the row's
//     toast would have gone.
//   onCancelAction(ev, card, s)  — absent → dismiss only.
// Both buttons remove the card afterwards (actionCard marks the removal
// root, the rootSlide idiom).
export const ActionNotification = {
  extends: 'Navbar',
  theme: 'canvas-card-glass',
  round: 'radiusControl',
  overflow: 'hidden',
  padding: '0',
  flow: 'x',
  align: 'stretch',
  gap: '0',
  width: '100%',
  maxWidth: 'H2',
  actionCard: true,
  state: {
    title: 'shell.actionNotification.done',
    descr: '',
    confirmText: 'shell.actionNotification.goTo',
    cancelText: 'common.action.cancel'
  },
  Hgroup: {
    flex: 1,
    margin: 'auto',
    padding: 'Z1 A Z1 B',
    gap: 'Y1',
    lineHeight: 1.4,
    minWidth: '0',
    H: {
      tag: 'strong',
      text: (el, s) => el.call('polyglot', s.title || '')
    },
    P: {
      if: (el, s) => !!s.descr,
      text: (el, s) => el.call('polyglot', s.descr || '')
    }
  },
  ActionNotificationButtons: {}
}

// The VERTICAL trailing button set — the family's action bar turned 90°:
// primary (Confirm / Go to) above, quiet Cancel below, flush right edge.
// Same N-action override contract as TutorialNotificationButtons: consumers
// replace `children` with { icon?, text, onClick, hide? } rows.
export const ActionNotificationButtons = {
  childExtends: ['CanvasButton', 'Button'],
  flow: 'y',
  align: 'stretch',
  fontSize: 'Z2',
  // gap, never inter-button dividers (§2.5 — row separators inside rounded
  // cards stay banned).
  gap: 'X2',
  flex: 'none',
  childProps: {
    round: '0',
    flex: 1,
    padding: 'Y A',
    gap: 'X2',
    align: 'center start'
  },
  children: [
    {
      icon: 'checkmark',
      text: (el, s) => el.call('polyglot', s.confirmText || 'shell.actionNotification.goTo'),
      onClick: (ev, el, s) => {
        const card = el.lookup((x) => x.actionCard)
        if (card && typeof card.onConfirmAction === 'function') {
          card.onConfirmAction(ev, card, s)
        } else {
          // The default: route to the notification's own target — feature
          // modal or slot, the ONE click-through decision the toast uses.
          try {
            el.call('openNotificationTarget', s, card || el)
          } catch (_) {
            /* target-less state — the dismiss below still resolves the card */
          }
        }
        if (card && card.remove) card.remove()
      }
    },
    {
      icon: 'x',
      text: (el, s) => el.call('polyglot', s.cancelText || 'common.action.cancel'),
      onClick: (ev, el, s) => {
        const card = el.lookup((x) => x.actionCard)
        if (card && typeof card.onCancelAction === 'function') {
          card.onCancelAction(ev, card, s)
        }
        if (card && card.remove) card.remove()
      }
    }
  ]
}
